import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  REPORTS_BY_SOURCE,
  UPLOAD_SOURCES,
  type UploadReport,
  type UploadSource,
} from "@/lib/uploadConstants";
import {
  downloadLatestUpload,
  fetchUploadLogs,
  type UploadLogStatus,
} from "@/services/blobApi";

const ANY = "__any__";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: UploadLogStatus; label: string }[] = [
  { value: "success", label: "Success" },
  { value: "validation_failed", label: "Validation failed" },
  { value: "duplicate_detected", label: "Duplicate detected" },
  { value: "upload_failed", label: "Unexpected error" },
];

function statusVariant(
  status: UploadLogStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "success":
      return "default";
    case "duplicate_detected":
      return "secondary";
    case "validation_failed":
    case "upload_failed":
      return "destructive";
    default:
      return "outline";
  }
}

function statusLabel(status: UploadLogStatus): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UploadLogsTab() {
  const { toast } = useToast();
  const [source, setSource] = useState<UploadSource | "">("");
  const [report, setReport] = useState<UploadReport | "">("");
  const [status, setStatus] = useState<UploadLogStatus | "">("");
  const [uploadedBy, setUploadedBy] = useState("");
  const [debouncedUploadedBy, setDebouncedUploadedBy] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const trimmed = uploadedBy.trim();
    if (trimmed === debouncedUploadedBy) return;
    const handle = window.setTimeout(() => {
      setDebouncedUploadedBy(trimmed);
      setPage(0);
    }, 2000);
    return () => window.clearTimeout(handle);
  }, [uploadedBy, debouncedUploadedBy]);

  const availableReports = source ? REPORTS_BY_SOURCE[source] : [];

  const params = useMemo(
    () => ({
      source: source || undefined,
      report: report || undefined,
      status: status || undefined,
      uploaded_by: debouncedUploadedBy || undefined,
      skip: page * pageSize,
      limit: pageSize,
    }),
    [source, report, status, debouncedUploadedBy, page, pageSize],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["uploadLogs", params],
    queryFn: () => fetchUploadLogs(params),
    placeholderData: keepPreviousData,
  });

  // Reuses the same /blob/download/{id} stream the Latest uploads tab calls
  // through — admin-only, keyed on the log entry's own id. A row whose raw
  // file has since been pruned surfaces as a 404 toast rather than a silent
  // broken click.
  const downloadMutation = useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      downloadLatestUpload(id, filename),
    onError: (err: Error) => {
      toast({
        title: "Download failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const downloadingId = downloadMutation.isPending
    ? downloadMutation.variables?.id ?? null
    : null;

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  const resetFilters = () => {
    setSource("");
    setReport("");
    setStatus("");
    setUploadedBy("");
    setDebouncedUploadedBy("");
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Upload history</h2>
            <p className="text-xs text-muted-foreground">
              Review manual file uploads and their processing status.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </header>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="log-source">Source</Label>
            <Select
              value={source || ANY}
              onValueChange={(v) => {
                setSource(v === ANY ? "" : (v as UploadSource));
                setReport("");
                setPage(0);
              }}
            >
              <SelectTrigger id="log-source">
                <SelectValue placeholder="Any source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any source</SelectItem>
                {UPLOAD_SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="log-report">Report</Label>
            <Select
              value={report || ANY}
              onValueChange={(v) => {
                setReport(v === ANY ? "" : (v as UploadReport));
                setPage(0);
              }}
              disabled={!source}
            >
              <SelectTrigger id="log-report">
                <SelectValue placeholder={source ? "Any report" : "Pick source first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any report</SelectItem>
                {availableReports.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="log-status">Status</Label>
            <Select
              value={status || ANY}
              onValueChange={(v) => {
                setStatus(v === ANY ? "" : (v as UploadLogStatus));
                setPage(0);
              }}
            >
              <SelectTrigger id="log-status">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any status</SelectItem>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="log-uploaded-by">Uploaded by</Label>
            <Input
              id="log-uploaded-by"
              type="email"
              placeholder="user@example.com"
              value={uploadedBy}
              onChange={(e) => setUploadedBy(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            disabled={!source && !report && !status && !uploadedBy}
          >
            Clear filters
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card">
        <div className="overflow-x-auto pr-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Uploaded at</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Report</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Uploaded by</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[90px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading upload logs…
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-sm text-destructive">
                    <AlertCircle className="mr-2 inline h-4 w-4" />
                    {(error as Error)?.message ?? "Failed to load upload logs"}
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                    No upload logs match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(row.uploaded_at)}
                    </TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[180px] truncate"
                        title={row.filename}
                      >
                        {row.filename}
                      </span>
                    </TableCell>
                    <TableCell>{row.source}</TableCell>
                    <TableCell className="max-w-[220px] truncate" title={row.report}>
                      {row.report}
                    </TableCell>
                    <TableCell>{row.year ?? "—"}</TableCell>
                    <TableCell className="max-w-[220px] truncate" title={row.uploaded_by}>
                      {row.uploaded_by}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant={statusVariant(row.status)}
                          className="whitespace-nowrap"
                        >
                          {statusLabel(row.status)}
                        </Badge>
                        {row.error_message && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-label="View error details"
                              >
                                <Info className="h-4 w-4" aria-hidden />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-80">
                              <p className="text-xs font-semibold text-foreground">
                                Error details
                              </p>
                              <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                                {row.error_message}
                              </p>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          downloadMutation.mutate({
                            id: row.id,
                            filename: row.filename,
                          })
                        }
                        disabled={downloadingId === row.id}
                        title="Download file"
                        aria-label={`Download ${row.filename}`}
                      >
                        {downloadingId === row.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <footer className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {total === 0
              ? "0 entries"
              : `Showing ${from}–${to} of ${total}`}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label
                htmlFor="log-page-size"
                className="text-xs text-muted-foreground"
              >
                Rows per page
              </Label>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(0);
                }}
              >
                <SelectTrigger id="log-page-size" className="h-8 w-[72px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isFetching}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages || isFetching}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
